/**
 * What each role package is allowed to *do* in this frontend.
 *
 * The backend is the authority — every endpoint enforces its own rules, and a user who submits a
 * forbidden write gets a 403 whatever this file says. This map exists for the other half: not
 * offering a control that can only fail.
 *
 * Note what this is **not**: a rule about which *pages* a role may open. Administration is reachable
 * by every role, deliberately — a read-only user can read their organization's users, and the page
 * simply renders without its write controls. An earlier version gated the route instead; that was
 * reversed on 2026-08-19 after a product-owner ruling, and the backend has confirmed it has no gate
 * of its own on reading users either.
 *
 * **Features are verbs, not resources.** This is the thing to understand before editing: the backend
 * grants a role `Can_Update`, not "may update devices". A verb therefore applies to *every* resource
 * the caller's organization scope reaches, and the only per-resource rules are a short list of
 * operations reserved for a system administrator. An earlier version of this file modelled
 * `manage_users` / `manage_organizations` / `manage_devices`, which cannot express any of that and was
 * wrong in most of its cells — it made Support look read-only when it can update, and let an
 * Administrator offer a create that is refused.
 *
 * The verb sets below are the seeded truth from the backend's `role_package_feature` table, supplied
 * 2026-08-19. They are still a copy, and a copy can drift: exposing the feature list on
 * `RolePackageResponseDto` is agreed in principle and ticketed separately. When it lands, derive from
 * the data and delete `ROLE_VERBS`.
 */

/** The write verbs the UI gates on. `Can_Get`, `Can_Import`, `Can_Export` and `Can_Run` gate nothing yet. */
export type Verb = 'create' | 'update' | 'delete';

/**
 * Granted verbs per role, from `InitialDataInserts.sql`.
 *
 * A role **absent from this map gets nothing**, so a new backend role can read everything and change
 * nothing until someone adds it — the safe direction to fail, and the visible prompt.
 */
const ROLE_VERBS: Readonly<Record<string, readonly Verb[]>> = {
  // Holds the `SystemAdmin` feature, which bypasses every check. Listed for completeness; the
  // service short-circuits on the token claim before consulting this map.
  'System Administrator': ['create', 'update', 'delete'],
  // Can_Get, Can_Create, Can_Update, Can_Delete, Can_Import, Can_Export, Can_Run.
  'Administrator':        ['create', 'update', 'delete'],
  // Can_Get, Can_Update, Can_Run.
  'Technician':           ['update'],
  // Can_Get, Can_Update, Can_Run — **identical to Technician**. Support is not a read-only role.
  'Support':              ['update'],
  // Can_Get, Can_Export. Writes nothing at all.
  'Full Read':            [],
};

/**
 * A control the UI gates. Named `<resource>.<operation>` so a call site reads as what it guards.
 *
 * The last two are **fields** rather than operations: the backend reserves a device's `deviceTypeId`
 * and an organization's `organizationPriorityLevelId` for a system administrator regardless of the
 * caller's verbs, so the surrounding dialog can be usable while those two inputs are not.
 */
export type Action =
  | 'user.invite'
  | 'user.update'
  | 'user.delete'
  | 'organization.create'
  | 'organization.update'
  | 'organization.delete'
  | 'device.create'
  | 'device.update'
  | 'device.type'
  | 'organization.tier';

interface ActionSpec {
  /** The verb the caller's role must hold. Ignored when `systemAdminOnly` is set. */
  verb: Verb;
  /**
   * Reserved for a system administrator whatever the role grants. `POST /Device` and organization
   * create/delete carry `[RoleFeature(SystemAdmin)]` on the action itself, so the caller's own
   * organization does not enter into it — an Administrator is refused in their own org exactly as in
   * a descendant.
   */
  systemAdminOnly?: boolean;
}

const ACTIONS: Readonly<Record<Action, ActionSpec>> = {
  'user.invite':          { verb: 'create' },
  'user.update':          { verb: 'update' },
  'user.delete':          { verb: 'delete' },
  'organization.create':  { verb: 'create', systemAdminOnly: true },
  'organization.update':  { verb: 'update' },
  'organization.delete':  { verb: 'delete', systemAdminOnly: true },
  // Real devices arrive through the provisioning Lambda; this endpoint is a manual fix-up hatch.
  'device.create':        { verb: 'create', systemAdminOnly: true },
  'device.update':        { verb: 'update' },
  'device.type':          { verb: 'update', systemAdminOnly: true },
  'organization.tier':    { verb: 'update', systemAdminOnly: true },
};

/** The verbs a role package holds. An unknown or absent name yields none. */
export function verbsFor(roleName: string | null | undefined): readonly Verb[] {
  if (roleName == null) return [];
  return ROLE_VERBS[roleName] ?? [];
}

/**
 * Whether a role package may perform `action` — **ignoring** the system-administrator bypass, which
 * belongs to `PermissionsService` because it comes from the token rather than the role name.
 */
export function roleAllows(roleName: string | null | undefined, action: Action): boolean {
  const spec = ACTIONS[action];
  if (spec.systemAdminOnly === true) return false;
  return verbsFor(roleName).includes(spec.verb);
}

/** Whether `action` is reserved for a system administrator whatever the role grants. */
export function isSystemAdminOnly(action: Action): boolean {
  return ACTIONS[action].systemAdminOnly === true;
}

/**
 * The backend name of the role that bypasses every check. `ROLE_VERBS` and `ROLE_METADATA` are both
 * keyed by this string, so it is named once here rather than spelled out at each use.
 */
export const SYSTEM_ADMINISTRATOR_ROLE = 'System Administrator';

/** Every gated action, for callers that need to weigh roles against the whole set. */
const ALL_ACTIONS = Object.keys(ACTIONS) as Action[];

/**
 * How many of the gated actions a role may perform — a measure of how much access it carries, used
 * to order roles from most to least. It gates nothing; `roleAllows` remains the only authority on
 * whether a control is offered.
 *
 * The system administrator is scored against the full set, including the `systemAdminOnly` actions
 * `roleAllows` refuses on principle. That bypass is the entire difference between it and
 * Administrator, which holds the same three verbs, so a score built from `roleAllows` alone would
 * rank the two equal.
 *
 * Technician and Support score the same because their verb sets are identical by design — see
 * `ROLE_VERBS`. Callers that need a total order must break that tie themselves.
 */
export function accessBreadth(roleName: string | null | undefined): number {
  if (roleName === SYSTEM_ADMINISTRATOR_ROLE) return ALL_ACTIONS.length;
  return ALL_ACTIONS.filter(action => roleAllows(roleName, action)).length;
}
